/**
 * Quick-Add Drawer
 *
 * Single-product quick-add from any product card.
 * "+" button on cards opens a drawer with product info, color swatches,
 * size selection, and add-to-cart.
 *
 * Pattern: complementary-look.js (Web Component, body-appended drawer,
 * size grid, ATC flow, cart drawer integration)
 */

if (!customElements.get('quick-add-drawer')) {
  customElements.define(
    'quick-add-drawer',
    class extends HTMLElement {
      constructor() {
        super();
        this.cache = new Map();
        this.selectedVariantId = null;
        this.currentProduct = null;
        this.currentHandle = null;
        this.currentColorLabel = null;
        this.currentColorSwatch = null;
        this.panel = null;
        this.body = null;
        this.footer = null;
        this._openedBy = null;
        this._escapeHandler = null;
        // DEV-2026-0384: Mehrfachpacks (Option "Menge")
        this.packIdx = -1;
        this.packSizeIdx = -1;
        this.packVariantsList = [];
        this.selectedSize = null;
        this.selectedPack = null;
        this.currentPackUnit = 'Paar';
      }

      connectedCallback() {
        this.shippingPolicyUrl = document.body.dataset.shippingPolicyUrl || '/policies/shipping-policy';
        this.buildDrawerShell();
        this.bindGlobalEvents();
      }

      /* ----------------------------------------------------------------
         Drawer Shell (static HTML, built once)
         ---------------------------------------------------------------- */

      buildDrawerShell() {
        this.innerHTML = `
          <div class="qa-drawer" role="dialog" aria-modal="true" aria-label="Schnellansicht">
            <div class="qa-drawer__backdrop"></div>
            <div class="qa-drawer__panel">
              <div class="qa-drawer__drag-handle"></div>
              <div class="qa-drawer__header">
                <button type="button" class="qa-drawer__close" aria-label="Schließen">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
              <div class="qa-drawer__body"></div>
              <div class="qa-drawer__footer"></div>
              <div class="qa-drawer__sr-status visually-hidden" aria-live="polite"></div>
            </div>
          </div>
        `;

        this.drawer = this.querySelector('.qa-drawer');
        this.panel = this.querySelector('.qa-drawer__panel');
        this.body = this.querySelector('.qa-drawer__body');
        this.footer = this.querySelector('.qa-drawer__footer');
        this.srStatus = this.querySelector('.qa-drawer__sr-status');
      }

      /* ----------------------------------------------------------------
         Event Binding
         ---------------------------------------------------------------- */

      bindGlobalEvents() {
        // Global click delegation for .qa-trigger buttons + card image navigation
        document.addEventListener('click', (e) => {
          // 1) Quick-Add Button → open drawer
          // DEV-2026-0169: 0/1 Variante → direkt in den Warenkorb ohne Drawer
          const trigger = e.target.closest('.qa-trigger');
          if (trigger) {
            e.preventDefault();
            e.stopPropagation();
            if (trigger.dataset.singleVariantId) {
              this.directAdd(trigger);
            } else {
              this.open(trigger);
            }
            return;
          }

          // 2) Card image area (.card__inner) → navigate to PDP
          const inner = e.target.closest('.card__inner');
          if (inner) {
            const wrapper = inner.closest('.card-wrapper');
            if (wrapper) {
              const link = wrapper.querySelector('.card__heading a[href]');
              if (link) {
                if (e.metaKey || e.ctrlKey) {
                  window.open(link.href, '_blank');
                } else {
                  window.location.href = link.href;
                }
              }
            }
          }
        });

        // Drawer internal click delegation
        this.drawer.addEventListener('click', (e) => {
          if (e.target.closest('.qa-drawer__backdrop') || e.target.closest('.qa-drawer__close')) {
            this.close();
          } else if (e.target.closest('.qa-drawer__pack:not(.is-unavailable)')) {
            this.selectPack(e.target.closest('.qa-drawer__pack'));
          } else if (e.target.closest('.qa-drawer__size:not(.is-unavailable)')) {
            this.selectSize(e.target.closest('.qa-drawer__size'));
          } else if (e.target.closest('.qa-drawer__atc:not(:disabled)')) {
            this.addToCart();
          }
        });

        this._escapeHandler = (e) => {
          if (e.key === 'Escape') this.close();
        };
      }

      /* ----------------------------------------------------------------
         Open / Close
         ---------------------------------------------------------------- */

      async open(trigger) {
        const handle = trigger.dataset.productHandle;
        if (!handle) return;

        this._openedBy = trigger;
        this.currentHandle = handle;
        this.selectedVariantId = null;

        this.currentColorLabel = trigger.dataset.colorLabel || null;
        this.currentColorSwatch = trigger.dataset.colorSwatch || null;
        this.currentBadgeText = trigger.dataset.badgeText || null;
        this.currentBadgeClass = trigger.dataset.badgeClass || null;
        this.currentFeatureText = trigger.dataset.featureText || null;
        this.currentPackUnit = trigger.dataset.packUnit || 'Paar';
        this.currentPackWish = Number(trigger.dataset.packWish) || 0; // DEV-2026-0450
        this.selectedSize = null;
        this.selectedPack = null;

        // Show drawer with skeleton – 2-step open for smooth transition:
        // Step 1: visibility: visible (panel still at translateY/X 100%)
        this.showSkeleton();
        this.drawer.setAttribute('open', '');
        this.panel.offsetHeight; // Force reflow so browser registers visibility before transform
        // Step 2: trigger slide-in + backdrop fade via .is-active class
        this.drawer.classList.add('is-active');
        // iOS-safe scroll lock: save position + fix body
        document.body.dataset.scrollY = window.scrollY;
        document.body.style.top = '-' + window.scrollY + 'px';
        document.body.classList.add('overflow-hidden');
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.addEventListener('keyup', this._escapeHandler);

        if (this.srStatus) this.srStatus.textContent = 'Produkt wird geladen…';

        // Fetch product data
        const product = await this.fetchProduct(handle);
        if (!product) {
          this.close();
          return;
        }

        this.currentProduct = product;
        this.renderProduct(product);

        if (this.srStatus) this.srStatus.textContent = `${product.title} – Schnellansicht geöffnet`;

        // Focus trap
        if (typeof trapFocus === 'function') {
          trapFocus(this.panel, this.panel.querySelector('.qa-drawer__close'));
        }
      }

      close() {
        this.drawer.classList.remove('is-active');  // Trigger reverse animation
        this.drawer.removeAttribute('open');         // visibility: hidden after 0.3s delay
        // iOS-safe scroll lock: restore position
        const scrollY = parseInt(document.body.dataset.scrollY || '0', 10);
        document.body.classList.remove('overflow-hidden');
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
        window.scrollTo(0, scrollY);
        document.removeEventListener('keyup', this._escapeHandler);

        if (this.srStatus) this.srStatus.textContent = 'Schnellansicht geschlossen';

        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(this._openedBy);
        }
      }

      /* ----------------------------------------------------------------
         Data Fetching
         ---------------------------------------------------------------- */

      async fetchProduct(handle) {
        if (this.cache.has(handle)) return this.cache.get(handle);
        try {
          const res = await fetch(`/products/${handle}.js`);
          if (!res.ok) throw new Error(res.statusText);
          const data = await res.json();
          this.cache.set(handle, data);
          return data;
        } catch (e) {
          console.error('[quick-add-drawer] fetch error:', e);
          return null;
        }
      }

      /* ----------------------------------------------------------------
         Rendering
         ---------------------------------------------------------------- */

      showSkeleton(clearFooter = true) {
        this.body.innerHTML = `
          <div class="qa-drawer__skeleton">
            <div class="qa-drawer__skeleton-product">
              <div class="qa-drawer__skeleton-image"></div>
              <div class="qa-drawer__skeleton-text">
                <div class="qa-drawer__skeleton-line qa-drawer__skeleton-line--short"></div>
                <div class="qa-drawer__skeleton-line qa-drawer__skeleton-line--long"></div>
                <div class="qa-drawer__skeleton-line qa-drawer__skeleton-line--medium"></div>
                <div class="qa-drawer__skeleton-line qa-drawer__skeleton-line--short"></div>
              </div>
            </div>
            <div class="qa-drawer__skeleton-sizes">
              <div class="qa-drawer__skeleton-size"></div>
              <div class="qa-drawer__skeleton-size"></div>
              <div class="qa-drawer__skeleton-size"></div>
              <div class="qa-drawer__skeleton-size"></div>
              <div class="qa-drawer__skeleton-size"></div>
              <div class="qa-drawer__skeleton-size"></div>
            </div>
          </div>
        `;
        if (clearFooter) this.footer.innerHTML = '';
      }

      renderProduct(product) {
        const imgSrc = this.getImageSrc(product, 360);
        const color = this.currentColorLabel || this.getProductColor(product);
        this.setupPacks(product, color);
        const isPack = this.packIdx >= 0;
        const sizes = isPack ? this.getPackSizes() : this.getAvailableSizes(product, color);
        const priceVaries = !!product.price_varies;
        const price = priceVaries ? product.price_min : product.price;
        const comparePrice = product.compare_at_price;
        const hasSale = !priceVaries && comparePrice && comparePrice > price;
        const badge = this.getBadge(product);
        // SHOP-2026-0083: Geschenkgutschein = keine Variante braucht Versand. /products/x.js
        // liefert kein gift_card-Flag; wie auf der Produktseite kein MwSt.-/Versandhinweis.
        const isGiftCard =
          (product.variants || []).length > 0 && product.variants.every((v) => v.requires_shipping === false);
        const featureText = this.currentFeatureText;

        // Build body HTML
        let html = '';

        // Product info row
        html += `<div class="qa-drawer__product">
          <div class="qa-drawer__product-image">
            ${imgSrc ? `<img src="${imgSrc}" alt="${this.escapeHTML(product.title)}" loading="eager" width="360" height="480">` : ''}
          </div>
          <div class="qa-drawer__product-info">
            ${badge ? `<span class="qa-drawer__badge ${badge.cls}">${this.escapeHTML(badge.text)}</span>` : ''}
            ${product.type ? `<span class="qa-drawer__collection">${this.escapeHTML(product.type)}</span>` : ''}
            <h3 class="qa-drawer__product-name">
              <a href="${product.url}">${this.escapeHTML(product.title)}</a>
            </h3>
            ${featureText ? `<span class="qa-drawer__product-feature">${this.escapeHTML(featureText)}</span>` : ''}
            ${color ? `<span class="qa-drawer__product-color">${this.escapeHTML(color)}</span>` : ''}
            <div class="qa-drawer__product-price">
              ${hasSale
                ? `<span class="qa-drawer__price-sale">${this.formatPrice(price)}</span><s class="qa-drawer__price-compare">${this.formatPrice(comparePrice)}</s>`
                : priceVaries ? `ab ${this.formatPrice(price)}` : this.formatPrice(price)
              }
            </div>
            ${isGiftCard
              ? `<div class="qa-drawer__tax-note caption">Kommt sofort per Mail</div>`
              : `<div class="qa-drawer__tax-note caption">inkl. MwSt., zzgl. <a href="${this.shippingPolicyUrl}">Versandkosten</a></div>`
            }
          </div>
        </div>`;

        // Size grid
        html += `<div class="qa-drawer__separator"></div>`;
        if (!isPack || this.packSizeIdx >= 0) html += `<div class="qa-drawer__sizes">
          <div class="qa-drawer__sizes-header">
            <span class="qa-drawer__sizes-label">GRÖSSE:</span>
          </div>
          <div class="qa-drawer__sizes-grid">
            ${sizes.map((s) => `
              <button type="button"
                class="qa-drawer__size${!s.available ? ' is-unavailable' : ''}"
                ${isPack ? `data-size="${this.escapeHTML(s.label)}"` : `data-variant-id="${s.variantId}"`}
                ${!s.available ? 'disabled' : ''}
                aria-label="Größe ${this.escapeHTML(s.label)}${!s.available ? ' – ausverkauft' : ''}"
              >${this.escapeHTML(s.label)}</button>
            `).join('')}
          </div>
        </div>`;

        if (isPack) html += this.renderPackGrid();

        this.body.innerHTML = html;

        // Footer: nur der ATC-Button.
        // DEV-2026-0313: Die Leiste mit den uebrigen Farben des Produkts stand
        // hier darunter und nahm 198 von 678 px Panelhoehe ein — fast so viel
        // wie Bild, Name und Preis zusammen. Auf kleinen Geraeten blieb fuer die
        // Groessen zu wenig uebrig, genau darum ging es in diesem Ticket.
        // Farben werden ueber die Produktkarten in der Liste gewaehlt.
        const footerHTML = `
          <button type="button" class="qa-drawer__atc" disabled>
            <span class="qa-drawer__atc-text">GRÖSSE WÄHLEN</span>
            <span class="qa-drawer__atc-spinner"></span>
          </button>
        `;

        this.footer.innerHTML = footerHTML;

        if (isPack) {
          this.selectedPack = this.bestPackPreferred(null);
          this.updatePackUI();
        }
      }

      /* ----------------------------------------------------------------
         Size Selection
         ---------------------------------------------------------------- */

      selectSize(btn) {
        if (this.packIdx >= 0 && btn.dataset.size !== undefined) {
          this.selectPackSize(btn);
          return;
        }
        const wasActive = btn.classList.contains('is-active');

        // Deselect all siblings
        this.body.querySelectorAll('.qa-drawer__size').forEach((s) => {
          s.classList.remove('is-active');
        });

        if (wasActive) {
          this.selectedVariantId = null;
        } else {
          btn.classList.add('is-active');
          this.selectedVariantId = btn.dataset.variantId;
        }

        this.updateATCState();
      }

      /* ----------------------------------------------------------------
         DEV-2026-0384: Mehrfachpacks (Option "Menge")
         Größe + Menge ergeben die Variante. Vorausgewählt ist die größte
         verfügbare Menge; ist sie in der gewählten Größe ausverkauft, greift
         die nächstkleinere – wie auf der Produktseite (pack-preselect.js).
         Produkte ohne "Menge" laufen unverändert durch den alten Pfad.
         ---------------------------------------------------------------- */

      setupPacks(product, color) {
        const options = product.options || [];
        this.packIdx = options.findIndex((o) => this.getOptionName(o).trim() === 'menge');
        this.packSizeIdx = -1;
        this.packVariantsList = [];
        if (this.packIdx < 0) return;

        this.packSizeIdx = options.findIndex((o) => {
          const name = this.getOptionName(o);
          return name.includes('size') || name.includes('größe') || name.includes('groesse');
        });
        const colorIdx = options.findIndex((o) => {
          const name = this.getOptionName(o);
          return name.includes('color') || name.includes('farbe') || name.includes('colour');
        });
        const colorKey = colorIdx >= 0 ? `option${colorIdx + 1}` : null;
        this.packVariantsList = (product.variants || []).filter((v) => !colorKey || !color || v[colorKey] === color);

        // Nur rein numerische Mengen ("6", "10") – sonst bleibt es beim bisherigen Drawer.
        const key = this.packKey();
        const allNumeric = this.packVariantsList.length > 0 && this.packVariantsList.every((v) => Number(v[key]) > 0);
        if (!allNumeric) {
          this.packIdx = -1;
          this.packVariantsList = [];
        }
      }

      packKey() {
        return `option${this.packIdx + 1}`;
      }

      sizeKey() {
        return this.packSizeIdx >= 0 ? `option${this.packSizeIdx + 1}` : null;
      }

      getPackSizes() {
        const key = this.sizeKey();
        if (!key) return [];
        const sizes = [];
        this.packVariantsList.forEach((v) => {
          let entry = sizes.find((s) => s.label === v[key]);
          if (!entry) {
            entry = { label: v[key], available: false };
            sizes.push(entry);
          }
          if (v.available) entry.available = true;
        });
        return sizes;
      }

      getPackValues() {
        const key = this.packKey();
        const values = [];
        this.packVariantsList.forEach((v) => {
          if (values.indexOf(v[key]) === -1) values.push(v[key]);
        });
        return values.filter((value) => Number(value) > 0);
      }

      // Varianten zu einer Menge, eingeschränkt auf die gewählte Größe (falls gewählt)
      packCandidates(pack, size) {
        const packKey = this.packKey();
        const sizeKey = this.sizeKey();
        return this.packVariantsList.filter(
          (v) => v[packKey] === pack && (!size || !sizeKey || v[sizeKey] === size)
        );
      }

      findPackVariant(size, pack) {
        if (this.sizeKey() && !size) return null;
        return this.packCandidates(pack, size)[0] || null;
      }

      isPackAvailable(pack, size) {
        return this.packCandidates(pack, size).some((v) => v.available);
      }

      // Größte verfügbare Menge zur Größe, optional höchstens maxQty
      bestPack(size, maxQty) {
        const values = this.getPackValues().sort((a, b) => Number(b) - Number(a));
        return (
          values.find((value) => (!maxQty || Number(value) <= maxQty) && this.isPackAvailable(value, size)) || null
        );
      }

      // DEV-2026-0450: Wunschmenge aus custom.vorauswahl_menge, ausverkauft -> nächstkleinere,
      // gibt es keine kleinere -> größte verfügbare. Ohne Wunsch: größte verfügbare Menge.
      bestPackPreferred(size) {
        const wish = this.currentPackWish || 0;
        return (wish && this.bestPack(size, wish)) || this.bestPack(size, 0);
      }

      // Preis einer Menge: gewählte Größe, sonst günstigste verfügbare Variante
      packPrice(pack, size) {
        const candidates = this.packCandidates(pack, size);
        const pool = candidates.filter((v) => v.available);
        const list = pool.length ? pool : candidates;
        if (!list.length) return null;
        return Math.min.apply(null, list.map((v) => v.price));
      }

      formatUnitPrice(cents) {
        return (Math.round(cents) / 100).toFixed(2).replace('.', ',') + ' \u20AC';
      }

      packLabel(value) {
        return Number(value) === 1 ? 'Einzeln' : `${this.escapeHTML(value)}er Pack`;
      }

      renderPackGrid() {
        const buttons = this.getPackValues()
          .map(
            (value) => `
            <button type="button" class="qa-drawer__pack" data-pack="${this.escapeHTML(value)}">
              <span class="qa-drawer__pack-badge" hidden>Bester Preis</span>
              <span class="qa-drawer__pack-name">${this.packLabel(value)}</span>
              <span class="qa-drawer__pack-unit"></span>
            </button>`
          )
          .join('');
        return `<div class="qa-drawer__sizes qa-drawer__packs">
          <div class="qa-drawer__sizes-header">
            <span class="qa-drawer__sizes-label">MENGE:</span>
          </div>
          <div class="qa-drawer__packs-grid">${buttons}</div>
        </div>`;
      }

      updatePackUI() {
        const size = this.selectedSize;
        const unit = this.escapeHTML(this.currentPackUnit);
        const buttons = Array.prototype.slice.call(this.body.querySelectorAll('.qa-drawer__pack'));

        let best = null;
        let worst = null;
        const unitPrices = {};
        buttons.forEach((btn) => {
          const pack = btn.dataset.pack;
          const price = this.packPrice(pack, size);
          if (price === null) return;
          unitPrices[pack] = price / Number(pack);
          if (this.isPackAvailable(pack, size)) {
            if (best === null || unitPrices[pack] < best) best = unitPrices[pack];
            if (worst === null || unitPrices[pack] > worst) worst = unitPrices[pack];
          }
        });

        buttons.forEach((btn) => {
          const pack = btn.dataset.pack;
          const available = this.isPackAvailable(pack, size);
          btn.classList.toggle('is-unavailable', !available);
          btn.disabled = !available;
          btn.classList.toggle('is-active', pack === this.selectedPack);
          btn.setAttribute('aria-pressed', pack === this.selectedPack ? 'true' : 'false');
          btn.setAttribute('aria-label', `${this.packLabel(pack)}${!available ? ' – ausverkauft' : ''}`);
          const unitEl = btn.querySelector('.qa-drawer__pack-unit');
          if (unitEl) unitEl.textContent = unitPrices[pack] !== undefined ? `${this.formatUnitPrice(unitPrices[pack])} / ${this.currentPackUnit}` : '';
          const badge = btn.querySelector('.qa-drawer__pack-badge');
          if (badge) badge.hidden = !(available && best !== null && best < worst && unitPrices[pack] === best);
        });

        // Preis im Kopf zeigt die gewählte Menge statt "ab …"
        const priceEl = this.body.querySelector('.qa-drawer__product-price');
        const price = this.selectedPack ? this.packPrice(this.selectedPack, size) : null;
        if (priceEl && price !== null) priceEl.textContent = this.formatPrice(price);

        const variant = this.selectedPack ? this.findPackVariant(size, this.selectedPack) : null;
        this.selectedVariantId = variant && variant.available ? String(variant.id) : null;
        this.updateATCState();
      }

      selectPackSize(btn) {
        const wasActive = btn.classList.contains('is-active');
        this.body.querySelectorAll('.qa-drawer__size').forEach((s) => s.classList.remove('is-active'));
        this.selectedSize = wasActive ? null : btn.dataset.size;
        if (!wasActive) btn.classList.add('is-active');

        if (this.selectedPack && !this.isPackAvailable(this.selectedPack, this.selectedSize)) {
          this.selectedPack =
            this.bestPack(this.selectedSize, Number(this.selectedPack)) || this.bestPack(this.selectedSize, 0);
        } else if (!this.selectedPack) {
          this.selectedPack = this.bestPackPreferred(this.selectedSize);
        }
        this.updatePackUI();
      }

      selectPack(btn) {
        this.selectedPack = btn.dataset.pack;
        this.updatePackUI();
      }

      updateATCState() {
        const atcBtn = this.footer.querySelector('.qa-drawer__atc');
        if (!atcBtn) return;

        const textEl = atcBtn.querySelector('.qa-drawer__atc-text');

        if (this.selectedVariantId) {
          atcBtn.disabled = false;
          textEl.textContent = 'IN DEN WARENKORB';
        } else {
          atcBtn.disabled = true;
          textEl.textContent = 'GRÖSSE WÄHLEN';
        }
      }

      /* ----------------------------------------------------------------
         Add to Cart
         ---------------------------------------------------------------- */

      async addToCart() {
        if (!this.selectedVariantId) return;

        const atcBtn = this.footer.querySelector('.qa-drawer__atc');
        atcBtn.classList.add('qa-drawer__atc--loading');
        atcBtn.disabled = true;

        try {
          const cartDrawer = document.querySelector('cart-drawer');
          const sectionIds = cartDrawer && cartDrawer.getSectionsToRender
            ? cartDrawer.getSectionsToRender().map((s) => s.id)
            : [];

          const reqBody = {
            items: [{ id: parseInt(this.selectedVariantId, 10), quantity: 1 }],
          };
          if (sectionIds.length > 0) {
            reqBody.sections = sectionIds.join(',');
            reqBody.sections_url = window.location.pathname;
          }

          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(reqBody),
          });

          if (!res.ok) throw new Error(res.statusText);

          const result = await res.json();

          // Publish cart update event
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'quick-add-drawer',
              cartData: result,
            });
          }

          // Success state
          atcBtn.classList.remove('qa-drawer__atc--loading');
          atcBtn.classList.add('qa-drawer__atc--success');
          atcBtn.querySelector('.qa-drawer__atc-text').textContent = 'HINZUGEFÜGT \u2713';

          setTimeout(() => {
            atcBtn.classList.remove('qa-drawer__atc--success');

            // Scroll-Position sichern BEVOR close() overflow-hidden entfernt
            const scrollY = window.scrollY || parseInt(document.body.dataset.scrollY || '0', 10);

            this.close();

            // Open cart drawer – scrollY direkt setzen damit open() den richtigen Wert liest
            if (cartDrawer) {
              document.body.dataset.scrollY = scrollY;
              if (cartDrawer.classList.contains('is-empty')) {
                cartDrawer.classList.remove('is-empty');
              }
              if (result.sections) {
                cartDrawer.renderContents(result);
              } else {
                this.fetchAndOpenDrawer(cartDrawer);
              }
            }
          }, 1500);
        } catch (e) {
          console.error('[quick-add-drawer] add to cart error:', e);
          atcBtn.classList.remove('qa-drawer__atc--loading');
          atcBtn.disabled = false;
        }
      }

      /* DEV-2026-0169: Direkt-ATC für Produkte mit 0/1 Variante — kein Drawer,
         Variante kommt als data-single-variant-id vom qa-trigger. Bei Fehler
         Fallback auf den regulären Drawer. */
      async directAdd(trigger) {
        if (trigger.classList.contains('is-loading')) return;
        trigger.classList.add('is-loading');

        try {
          const cartDrawer = document.querySelector('cart-drawer');
          const sectionIds = cartDrawer && cartDrawer.getSectionsToRender
            ? cartDrawer.getSectionsToRender().map((s) => s.id)
            : [];

          const reqBody = {
            items: [{ id: parseInt(trigger.dataset.singleVariantId, 10), quantity: 1 }],
          };
          if (sectionIds.length > 0) {
            reqBody.sections = sectionIds.join(',');
            reqBody.sections_url = window.location.pathname;
          }

          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(reqBody),
          });

          if (!res.ok) throw new Error(res.statusText);

          const result = await res.json();

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'quick-add-drawer',
              cartData: result,
            });
          }

          if (cartDrawer) {
            if (cartDrawer.classList.contains('is-empty')) {
              cartDrawer.classList.remove('is-empty');
            }
            if (result.sections) {
              cartDrawer.renderContents(result);
            } else {
              this.fetchAndOpenDrawer(cartDrawer);
            }
          }
        } catch (e) {
          console.error('[quick-add-drawer] direct add error:', e);
          this.open(trigger); // Fallback: regulärer Drawer
        } finally {
          trigger.classList.remove('is-loading');
        }
      }

      async fetchAndOpenDrawer(cartDrawer) {
        try {
          const sections = cartDrawer.getSectionsToRender().map((s) => s.id);
          const res = await fetch(
            `${window.location.pathname}?sections=${sections.join(',')}`
          );
          if (!res.ok) throw new Error(res.statusText);
          const data = await res.json();
          cartDrawer.renderContents({ sections: data });
        } catch (e) {
          console.error('[quick-add-drawer] cart drawer fallback error:', e);
        }
      }

      /* ----------------------------------------------------------------
         Helpers
         ---------------------------------------------------------------- */

      getBadge(product) {
        // Priority: Liquid-provided badge > JS-computed AUSVERKAUFT > JS-computed SPARE > NEU
        if (this.currentBadgeText) {
          return { text: this.currentBadgeText, cls: this.currentBadgeClass || '' };
        }

        // JS fallback: AUSVERKAUFT
        if (!product.available) {
          return { text: 'AUSVERKAUFT', cls: 'qa-drawer__badge--sold-out' };
        }

        // JS fallback: SPARE
        if (!product.price_varies && product.compare_at_price && product.compare_at_price > product.price) {
          const savings = this.formatPrice(product.compare_at_price - product.price);
          return { text: `SPARE ${savings}`, cls: 'qa-drawer__badge--sale' };
        }

        // JS fallback: NEU (based on created_at + settings.new_badge_days)
        const newDays = parseInt(this.dataset.newBadgeDays || '0', 10);
        if (newDays > 0 && product.created_at) {
          const created = new Date(product.created_at);
          const now = new Date();
          const diffDays = Math.floor((now - created) / 86400000);
          if (diffDays <= newDays) {
            return { text: 'NEU', cls: 'qa-drawer__badge--new' };
          }
        }

        return null;
      }

      getOptionName(o) {
        if (typeof o === 'string') return o.toLowerCase();
        if (o && o.name) return o.name.toLowerCase();
        return '';
      }

      getProductColor(product) {
        if (!product.variants || product.variants.length === 0) return '';
        const first = product.variants.find((v) => v.available) || product.variants[0];
        if (product.options) {
          const colorIdx = product.options.findIndex((o) => {
            const name = this.getOptionName(o);
            return name.includes('color') || name.includes('farbe') || name.includes('colour');
          });
          if (colorIdx >= 0) {
            return first[`option${colorIdx + 1}`] || '';
          }
        }
        return '';
      }

      getAvailableSizes(product, color) {
        if (!product.variants) return [];

        let sizeIdx = -1;
        let colorIdx = -1;
        if (product.options) {
          sizeIdx = product.options.findIndex((o) => {
            const name = this.getOptionName(o);
            return name.includes('size') || name.includes('größe') || name.includes('groesse');
          });
          colorIdx = product.options.findIndex((o) => {
            const name = this.getOptionName(o);
            return name.includes('color') || name.includes('farbe') || name.includes('colour');
          });
        }

        if (sizeIdx < 0) {
          // No size option – return all variants
          return product.variants.map((v) => ({
            label: v.title,
            variantId: v.id,
            available: v.available,
          }));
        }

        const sizeKey = `option${sizeIdx + 1}`;
        const colorKey = colorIdx >= 0 ? `option${colorIdx + 1}` : null;

        // Filter by color if available
        const filtered = colorKey && color
          ? product.variants.filter((v) => v[colorKey] === color)
          : product.variants;

        // Deduplicate by size
        const seen = new Set();
        return filtered
          .filter((v) => {
            const size = v[sizeKey];
            if (seen.has(size)) return false;
            seen.add(size);
            return true;
          })
          .map((v) => ({
            label: v[sizeKey],
            variantId: v.id,
            available: v.available,
          }));
      }

      getImageSrc(product, width) {
        let src = '';
        if (product.images && product.images.length > 0) {
          src = product.images[0].src || product.images[0];
        } else if (product.featured_image) {
          src = typeof product.featured_image === 'string'
            ? product.featured_image
            : product.featured_image.src || '';
        } else if (product.image && product.image.src) {
          src = product.image.src;
        }
        if (!src) return '';
        return this.resizeImage(src, width);
      }

      formatPrice(price) {
        const num = parseFloat(price);
        if (isNaN(num)) return '';
        return (num / 100).toFixed(2).replace('.', ',') + '\u20AC';
      }

      resizeImage(src, width) {
        if (!src) return '';
        const fullSrc = src.startsWith('//') ? 'https:' + src : src;
        try {
          const url = new URL(fullSrc);
          url.searchParams.set('width', width);
          return url.toString();
        } catch {
          return fullSrc;
        }
      }

      escapeHTML(str) {
        if (!str) return '';
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
      }
    }
  );
}
